"use client";

import React from "react";
import { FarminstaIntroLayout } from "@/app/home/landing-page";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/app/home/animated-section";
import { ArrowRight, Check } from "lucide-react";
import Image from "next/image";
import img1 from '../public/images/WebsiteImages/fcm/1.svg'
import { useDemoModal } from "@/hooks/useDemoModal";



// Hero Section with left-right layout
const MPHeroSection: React.FC = () => {
  const { openModal, DemoModal } = useDemoModal();

  const handleDemo = () => {
    openModal();
  }
  return (
    <>
    <section className="relative pt-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-[#ebf2f0]">
      {/* Background gradient */}
      <div className="absolute top-[-100px] right-[-200px] w-[600px] h-[600px] bg-gradient-radial from-[#FBE8E0] to-transparent opacity-60 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-20 items-center relative">
          {/* Left Content */}
          <div>
            {/* <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white border border-primary rounded-full mb-7">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
              <span className="text-[11.5px] font-medium text-primary uppercase tracking-wider">
                Marketing Pod · Kharif Cohort 2026
              </span>
            </div> */}

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1612] mb-7 leading-tight tracking-tight">
              Run your Kharif on a{" "}
              <em className="italic font-medium">Marketing Pod.</em>
            </h1>

            <p className="text-lg sm:text-xl leading-relaxed mb-10 max-w-xl">
              One dedicated women voice agent. Six channels. 90 days of managed marketing built around your crop season — so every rupee of spend ties back to a farmer, an FO, and a sale.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Button
                size="lg"
                className="bg-primary hover:bg-secondary text-white px-5 py-4 text-base font-bold rounded-sm transition duration-300 ease-in-out transform hover:scale-105"
                onClick={handleDemo}
              >
                Book a Demo
              </Button>
              {/* <Button 
                size="lg"
                variant="outline"
                className=" px-7 py-6 text-base font-semibold border rounded-lg transition-all border"
                onClick={handleSeeHowItWorks}
              >
                See how it works ↓
              </Button> */}
            </div>

            {/* <div className="inline-flex items-center gap-3 px-4 py-2.5 bg-[#FBE8E0] rounded-lg text-sm text-[#B8431F] font-medium">
              <span>◐</span>
              <span>Applications close <strong className="font-semibold">May 31, 2026</strong> · Limited to 12 brands</span>
            </div> */}

            {/* Proof Strip */}
            {/* <div className="mt-6 pt-9 border-t border-[#E3DAD0]">
              <div className="flex items-center gap-8 flex-wrap">
                <span className="text-[11px] uppercase tracking-widest text-[#7A6E62] font-medium">Built by</span>
                <div className="flex gap-8 flex-wrap">
                  <div>
                    <strong className="text-2xl font-semibold text-[#1A1612] block leading-none">600K</strong>
                    <span className="text-xs text-[#7A6E62] mt-1 block">villages</span>
                  </div>
                  <div>
                    <strong className="text-2xl font-semibold text-[#1A1612] block leading-none">2M</strong>
                    <span className="text-xs text-[#7A6E62] mt-1 block">farmers</span>
                  </div>
                  <div>
                    <strong className="text-2xl font-semibold text-[#1A1612] block leading-none">42+</strong>
                    <span className="text-xs text-[#7A6E62] mt-1 block">agri brands</span>
                  </div>
                  <div>
                    <strong className="text-2xl font-semibold text-[#1A1612] block leading-none">1500+</strong>
                    <span className="text-xs text-[#7A6E62] mt-1 block">FOs daily</span>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          {/* Right - Engine Visualization */}
          <div className="relative w-full max-w-[500px] h-[450px] mx-auto lg:ml-auto">
            <div className="absolute inset-0 flex items-center justify-center">
              <Image
                className="h-[400px] w-[500px]"
                src={img1}
                alt="Ecosystem"

              />
            </div>

            {/* Caption below */}
            {/* <div className="text-center mt-5">
              <p className="text-xs font-semibold text-[#7A6E62] uppercase tracking-widest">
                One Engine · Six Channels
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </section>
    <DemoModal/>
    </>
  );
};

// Problem Section
const ProblemPanel: React.FC = () => {
  const problems = [
    {
      title: "FO activity is unverified",
      description: "You can't tell which farmer visits happened, which were quality, and which were paperwork."
    },
    {
      title: "Leads die in notebooks",
      description: "Farmers your FOs met never get a second call. The pipeline leaks every week of the season."
    },
    {
      title: "Channels don't talk",
      description: "WhatsApp, IVR, social, demos — five vendors, five reports, zero orchestration."
    },
    {
      title: "Spend can't be attributed",
      description: "You learn what worked after the season ends — when it's too late to redirect a single rupee."
    }
  ];

  return (
    <section className="py-10 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto relative">
        <span className="text-[14.5px] font-medium uppercase tracking-[0.18em] text-primary mb-6 inline-block">
          The Problem
        </span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium  mb-10 leading-tight tracking-tight">
          The marketing gap in agri-input is{" "}
          <em className="italic text-primary">measurable and expensive.</em>
        </h2>
        <p className="text-xl leading-relaxed mb-12 max-w-3xl">
          You spend on dealer schemes, IVR blasts, social campaigns, demo days, and field force salaries. At the end of the season, you have a sales number — but you can't trace it back to the spend.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {problems.map((problem, index) => (
            <div
              key={index}
              className="pl-4 border-l-4 border-red bg-white"
            >
              <strong className="block text-xl font-medium">{problem.title}</strong>
              <span className=" text-sm leading-relaxed">{problem.description}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Commitments Section
const OurServicesPanel: React.FC = () => {
  const commitments = [
    {
      num: "01",
      title: "Built around your crop season.",
      description: "Marketing Pod runs as a managed engagement aligned to your crop season — Kharif, Rabi, or Zaid. Defined arc, defined deliverables, defined closing report. You buy a season of orchestrated engagement, not a list of activities."
    },
    {
      num: "02",
      title: "Five channels, one orchestration.",
      description: "A dedicated women voice agent leads the pod — coordinating FO ground-truth, WhatsApp and SMS at scale, influencer credibility, and social amplification. Five channels working as one system around your field officers, not bolted on beside them."
    },
    {
      num: "03",
      title: "Every lead, traceable to a conversation.",
      description: "Recorded calls, verified farmer appointments, daily and weekly reports, and a closing season review. Every appointment handed to your field officers traces back to the conversation that produced it — audited pipeline, not reported activity."
    }
  ];

  return (
    <section className="py-4   px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-16">
        <span className="text-[14.5px] font-medium uppercase tracking-[0.18em] text-primary mb-6 inline-block">
            What you get
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-5 leading-tight tracking-tight">
            From field activity to audited farmer pipeline.
          </h2>
          <p className="text-lg text-[#7A6E62] leading-relaxed">
            Farminsta Marketing Pod is the managed layer that orchestrates voice, WhatsApp, influencers, and social around your field officers — every lead traceable to the conversation that produced it.          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-[-50]">
          {commitments.map((commitment, index) => (
            <div
              key={index}
              className="bg-white border border-[#E3DAD0] rounded-2xl p-4 hover:-translate-y-1 hover:border-primary hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-semibold text-[#1A1612] mb-4 leading-tight tracking-tight">
                {commitment.title}
              </h3>
              <p className="text-sm text-[#3D362F] leading-relaxed">
                {commitment.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Six Channels Engine Section
const UnifiedEnginePanel: React.FC = () => {
  const channels = [
    {
      num: "1",
      title: "Women Voice Agents",
      description: "Trained agri-professionals — graduates and post-graduates in agriculture — who own farmer conversations in your priority markets. Recorded calls. Verified appointments. Quality-audited engagement.",
      role: "The orchestrating layer. Connects every other channel to the farmer."
    },
    {
      num: "2",
      title: "Field Officer Coordination",
      description: "Your existing FOs, made measurable. Our women agents validate FO activity daily, sync appointment books, and convert field motion into reported outcomes.",
      role: "The ground-truth layer. Where digital intent becomes physical action."
    },
    {
      num: "3",
      title: "AI Sales Agent",
      description: "Always-on AI voice agent that calls farmers in regional languages — qualifies intent, books FO demos, recovers cold leads, and runs re-engagement campaigns. Every call transcribed, scored, and routed.",
      role: "The scale layer for voice. 10x the farmers a human team can reach — at a fraction of the cost per call."
    },
    {
      num: "4",
      title: "WhatsApp & SMS Engagement",
      description: "Broadcast campaigns built on segmented farmer lists. Two-way conversations. Drip sequences across the 90 days. Fully managed. Fully reported.",
      role: "The reach layer. Touches every farmer in every pod market — daily if needed."
    },
    {
      num: "5",
      title: "Influencer-Led Content",
      description: "Regional-language influencer videos featuring trusted local faces. Yours to keep and reuse after the programme ends. We handle scripting, production, and distribution.",
      role: "The credibility layer. Farmer-to-farmer voice that no brand-led ad can replicate."
    },
    {
      num: "6",
      title: "Social Media Amplification",
      description: "Hyper-targeted paid social and organic content layered onto your pod markets. Available as a built-in component or paid add-on depending on configuration.",
      role: "The awareness layer. Warms every district before voice and field touch it."
    }
  ];

  return (
    <section className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-16">
        <span className="text-[14.5px] font-medium uppercase tracking-[0.18em] text-primary mb-6 inline-block">
            How it comes together
          </span>
          <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-5 leading-tight tracking-tight">
            One engine. Six channels. Built for agri-input brands.
          </h2>
          <p className="text-lg text-[#7A6E62] leading-relaxed">
            Farminsta Marketing Pod is not a vendor with one trick. We operate a unified marketing engine that orchestrates six channels into a coordinated season activation — each channel reinforcing the others, all of them feeding the same outcome: qualified farmer leads, validated field activity, and measurable spend.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-[-50]">
          {channels.map((channel, index) => (
            <div
              key={index}
              className="bg-white border border-primary rounded-2xl p-9 relative hover:-translate-y-1 hover:border-primary transition-all duration-350 group flex flex-col"
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-8 right-8 h-[3px] bg-primary rounded-b-sm transform scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left"></div>

              <div className="w-11 h-11 bg-primary text-white rounded-lg flex items-center justify-center text-xl font-bold mb-6">
                {channel.num}
              </div>
              <h3 className="text-xl font-semibold text-[#1A1612] mb-3.5 leading-tight tracking-tight">
                {channel.title}
              </h3>
              <p className="text-sm text-[#3D362F] leading-relaxed mb-5 flex-grow">
                {channel.description}
              </p>
              <div className="pt-5 border-t border-dashed border-[#E3DAD0] mt-auto">
                <strong className="text-primary font-semibold text-[11px] uppercase tracking-wider block mb-1">
                  Role in the engine
                </strong>
                <p className="text-[13.5px] text-[#7A6E62] leading-relaxed">
                  {channel.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What Pod Does Section
// const InstallPodPanel: React.FC = () => {
//   const features = [
//     {
//       num: "01",
//       title: "Nurture your FO leads.",
//       description: "We don't let leads die in your FO's notebook. Our women agents call the farmers your field officers met recently — and the farmers you registered during the season — to identify the right candidates, recommend the next conversation, and fix appointments back with your FO.",
//       tagline: "Your existing pipeline, worked harder. Without you hiring more people."
//     },
//     {
//       num: "02",
//       title: "Generate new leads, multi-channel.",
//       description: "We bring new farmers into your funnel. Through social media performance marketing, regional influencer content, AI-driven outbound calls, WhatsApp, and SMS — we generate fresh farmer leads in your priority markets, nurture them through our women agents, and hand the qualified ones to your FOs for conversion.",
//       tagline: "Top-of-funnel and bottom-of-funnel, working as one machine."
//     },
//     {
//       num: "03",
//       title: "Quality-control your demand activity.",
//       description: "We watch what gets done, not just what gets reported. Daily validation of FO field activity. Market coverage tracking. Target progression against plan. Insight reports built for your sales managers — not abstract dashboards, but actionable scorecards on where the season is winning and where it's slipping.",
//       tagline: "The audit layer your marketing spend has never had."
//     },
//     {
//       num: "04",
//       title: "Engage your retailers.",
//       description: "We don't stop at the farmer. We close the loop at the counter. Our women agents engage with retailers across your priority markets — supporting product placement conversations, tracking stock movement, gathering collection signals, and surfacing liquidation roadblocks before they become season-end inventory problems.",
//       tagline: "When farmer demand meets retailer readiness, sales actually happen. We work both sides of that handshake."
//     }
//   ];

//   return (
//     <section className="py-6 px-4 sm:px-6 lg:px-8 bg-[#F7F2EC]">
//       <div className="max-w-6xl mx-auto">
//         <div className="max-w-3xl mb-16">
//           <span className="text-[11.5px] font-medium uppercase tracking-[0.18em] text-[#D85A30] mb-6 inline-block">
//             What a pod actually runs
//           </span>
//           <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-5 leading-tight tracking-tight">
//             Install a pod. <em className="italic text-[#D85A30] font-normal">Activate five channels.</em>
//           </h2>
//           <p className="text-lg text-[#7A6E62] leading-relaxed">
//             One pod installs your dedicated women voice agent across 5 FO markets. Five integrated channels light up around her — each working to move your season forward.
//           </p>
//         </div>

//         <div className="space-y-0">
//           {features.map((feature, index) => (
//             <div 
//               key={index}
//               className="grid md:grid-cols-[120px_1fr_1.2fr] gap-12 items-start py-12 border-t border-[#E3DAD0] last:border-b"
//             >
//               <div className="text-7xl font-normal italic text-[#D85A30] leading-none">
//                 {feature.num}
//               </div>
//               <h3 className="text-3xl font-semibold text-[#1A1612] leading-tight tracking-tight">
//                 {feature.title}
//               </h3>
//               <div>
//                 <p className="text-base text-[#3D362F] leading-relaxed mb-6">
//                   {feature.description}
//                 </p>
//                 <div className="p-4.5 border-l-[3px] border-[#D85A30]">
//                   <p className="text-lg italic text-[#B8431F] ml-2 font-medium leading-snug">
//                     {feature.tagline}
//                   </p>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// Final CTA Section
const FinalCTASection: React.FC = () => {
  const { openModal, DemoModal } = useDemoModal();
   
       const handleDemo = () => {
           openModal();
       }

  return (
    <>
    <section className="py-6 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute top-[-50%] right-[-20%] w-[80%] h-[200%] pointer-events-none"></div>

      <div className="max-w-3xl mx-auto text-center relative">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium  mb-6 leading-tight tracking-tight">
          Ready to <em className="italic font-normal opacity-80">run your season</em> with us?
        </h2>
        <p className="text-lg sm:text-xl leading-relaxed mb-10">
          Applications for the Kharif 2026 cohort close May 31. We onboard 12 brands per season — capacity is real, and it fills.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          {/* <Button 
            size="lg"
            className="bg-white text-[#D85A30] px-7 py-6 text-base font-semibold rounded-lg  border transition-all"
            onClick={handleApply}
          >
            Apply for Kharif Cohort 2026
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button> */}
          <Button
            size="lg"
            className="bg-primary hover:bg-secondary text-white px-7 py-6 text-base font-bold rounded-sm transition duration-300 ease-in-out transform hover:scale-105"
            onClick={handleDemo}
          >
            Talk to Pod Manager
          </Button>
        </div>
      </div>
    </section>
    <DemoModal/>
    </>
  );
};

// Main Component
export function MarketingPodContent() {
  return (
    <FarminstaIntroLayout currentPage="marketingPod">
      <MPHeroSection />
      <AnimatedSection>
        <ProblemPanel />
      </AnimatedSection>
      <AnimatedSection>
        <OurServicesPanel />
      </AnimatedSection>
      <AnimatedSection>
        <UnifiedEnginePanel />
      </AnimatedSection>
      {/* <AnimatedSection>
        <InstallPodPanel />
      </AnimatedSection> */}
      <AnimatedSection>
        <FinalCTASection />
      </AnimatedSection>
    </FarminstaIntroLayout>
  );
}
